import React, { useState, useEffect, useCallback } from 'react';
import CardItem from '../components/CardItem';
import EditModal from '../components/EditModal';


export default function TheBox() {
  const [cards, setCards] = useState([]);
  const [tags, setTags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [activeTag, setActiveTag] = useState('');
  const [editCard, setEditCard] = useState(null);
  const [selected, setSelected] = useState({});
  const [bulkTag, setBulkTag] = useState('');
  const [bulkMsg, setBulkMsg] = useState('');

  const fetchCards = useCallback(async () => {
    setLoading(true);
    const params = new URLSearchParams({ status: 'active' });
    if (search) params.set('search', search);
    if (activeTag) params.set('tag', activeTag);
    const res = await fetch(`/api/cards?${params}`, { credentials: 'include' });
    const data = await res.json();
    setCards(Array.isArray(data) ? data : []);
    setLoading(false);
  }, [search, activeTag]);

  const fetchTags = useCallback(async () => {
    const res = await fetch('/api/tags', { credentials: 'include' });
    const data = await res.json();
    setTags(Array.isArray(data) ? data : []);
  }, []);

  useEffect(() => { fetchCards(); }, [fetchCards]);
  useEffect(() => { fetchTags(); }, [fetchTags]);

  const handleAction = async (card, action) => {
    if (action === 'edit') { setEditCard(card); return; }
    if (action === 'archive') {
      await fetch(`/api/cards/${card.id}`, {
        method: 'PATCH', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: 'archived' })
      });
      fetchCards();
    }
    if (action === 'delete') {
      await fetch(`/api/cards/${card.id}`, {
        method: 'PATCH', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: 'deleted' })
      });
      fetchCards();
    }
  };

  const handleSaveEdit = (updated) => {
    setCards(cards.map(c => c.id === updated.id ? updated : c));
    setEditCard(null);
    fetchTags();
  };

  const selectedIds = Object.entries(selected).filter(([, v]) => v).map(([k]) => k);

  const handleBulkApply = async (remove = false) => {
    if (!bulkTag.trim() || selectedIds.length === 0) return;
    await fetch('/api/tags/bulk-apply', {
      method: 'POST', credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ cardIds: selectedIds, tagNames: [bulkTag.trim()], remove })
    });
    setBulkMsg(`Tag "${bulkTag.trim()}" ${remove ? 'removed from' : 'added to'} ${selectedIds.length} card(s).`);
    setBulkTag('');
    setSelected({});
    fetchCards();
    fetchTags();
    setTimeout(() => setBulkMsg(''), 3000);
  };


  return (
    <div>
      <div className="page-header">
        <h1>All Cards</h1>
        <div className="subtitle">{cards.length} card{cards.length !== 1 ? 's' : ''} in the active deck</div>
      </div>

      <div className="toolbar">
        <input
          className="search-input"
          placeholder="Search cards..."
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
      </div>

      {tags.length > 0 && (
        <div className="tag-filters">
          <span
            className={`tag ${!activeTag ? 'active' : ''}`}
            onClick={() => setActiveTag('')}
          >All</span>
          {tags.map(t => (
            <span
              key={t.id}
              className={`tag ${activeTag === t.name ? 'active' : ''}`}
              onClick={() => setActiveTag(activeTag === t.name ? '' : t.name)}
            >{t.name} ({t.card_count})</span>
          ))}
        </div>
      )}

      {loading ? (
        <div className="spinner" />
      ) : cards.length === 0 ? (
        <div className="empty-state">
          <div className="icon">—</div>
          <h3>No cards found</h3>
          <p>{search || activeTag ? 'Try clearing your filters.' : 'Add your first card to get started.'}</p>
        </div>
      ) : (
        <div className="cards-grid">
          {cards.map(card => (
            <CardItem
              key={card.id}
              card={card}
              onAction={handleAction}
              selectable={true}
              selected={!!selected[card.id]}
              onSelect={(id, checked) => setSelected(prev => ({ ...prev, [id]: checked }))}
            />
          ))}
        </div>
      )}

      {selectedIds.length > 0 && (
        <div className="bulk-action-bar">
          <span>{selectedIds.length} selected</span>
          <input
            value={bulkTag}
            onChange={e => setBulkTag(e.target.value)}
            placeholder="Tag name..."
            onKeyDown={e => e.key === 'Enter' && handleBulkApply(false)}
          />
          <button className="btn btn-success" onClick={() => handleBulkApply(false)}>Add Tag</button>
          <button className="btn btn-secondary" onClick={() => handleBulkApply(true)}>Remove Tag</button>
          <button className="btn btn-secondary" onClick={() => setSelected({})}>Clear</button>
          {bulkMsg && <span style={{ color: '#aaffaa' }}>{bulkMsg}</span>}
        </div>
      )}

      {editCard && (
        <EditModal card={editCard} onSave={handleSaveEdit} onClose={() => setEditCard(null)} />
      )}
    </div>
  );
}
